import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { ProductoActividad } from '../../lib/supabase';
import { ProduccionConMetricas } from '../../services/produccionService';
import { SelectorClientePedido } from '../SelectorClientePedido';
import { TextInput } from '../ui/TextInput';
import { Select } from '../ui/Select';
import { FORM_BORDER, FORM_BORDER_HOVER, FOCUS_RING, FORM_RADIUS } from '../ui/tokens';

interface Paso1ClienteProductoProps {
  // Productos
  productos: ProductoActividad[];
  productoId: string;
  onProductoChange: (id: string) => void;
  precioUnitario: number;
  
  // Lotes (producciones)
  producciones: ProduccionConMetricas[];
  produccionId: string;
  onProduccionChange: (id: string) => void;
  
  // Cliente
  tipoCliente: 'miembro' | 'externo';
  onTipoClienteChange: (tipo: 'miembro' | 'externo') => void;
  miembroId: string;
  onMiembroChange: (id: string, nombre?: string) => void;
  nombreExterno: string;
  onNombreExternoChange: (nombre: string) => void;
  miembroReferidoId: string;
  onMiembroReferidoChange: (id: string, nombre?: string) => void;
  lumisialId: string;
  initialMiembroNombre?: string;
  initialMiembroReferidoNombre?: string;
  
  // Cantidad
  cantidad: number;
  onCantidadChange: (cantidad: number) => void;
  
  // Fecha del pedido
  fechaVenta: string;
  onFechaVentaChange: (fecha: string) => void;
  
  // Modo edición
  isEditMode: boolean;

  // Sin asociación a miembro (p.ej. actividades sin JAL)
  sinAsociacionMiembro?: boolean;
}

export const Paso1ClienteProducto: React.FC<Paso1ClienteProductoProps> = ({
  productos,
  productoId,
  onProductoChange,
  precioUnitario,
  producciones,
  produccionId,
  onProduccionChange,
  tipoCliente,
  onTipoClienteChange,
  miembroId,
  onMiembroChange,
  nombreExterno,
  onNombreExternoChange,
  miembroReferidoId,
  onMiembroReferidoChange,
  lumisialId,
  initialMiembroNombre,
  initialMiembroReferidoNombre,
  cantidad,
  onCantidadChange,
  fechaVenta,
  onFechaVentaChange,
  isEditMode,
  sinAsociacionMiembro = false
}) => {
  const montoTotal = cantidad * precioUnitario;

  // Filtrar lotes activos del producto seleccionado
  const lotesDelProducto = productoId
    ? producciones.filter(p => p.producto_id === productoId && p.estado === 'activa')
    : [];
  const mostrarSelectorLote = lotesDelProducto.length > 1;
  const stockTotalDisponible = lotesDelProducto.reduce((sum, l) => sum + l.stock_disponible, 0);
  const sinStock = productoId !== '' && lotesDelProducto.length > 0 && stockTotalDisponible === 0;

  return (
    <div className="space-y-6">
      {/* Producto y Fecha en una fila */}
      <div className="flex gap-4">
        <div className="flex-1 min-w-0">
          <Select
            label="Producto"
            options={productos.map((producto: ProductoActividad) => ({
              value: producto.id,
              label: producto.nombre_producto,
            }))}
            value={productoId}
            onChange={onProductoChange}
            placeholder="Seleccionar producto..."
            disabled={isEditMode}
          />
        </div>

        <div className="w-[40%] flex-shrink-0">
          <label htmlFor="paso1-fecha-venta" className="block text-sm font-medium text-slate-600 mb-1.5">
            Fecha
          </label>
          <input
            id="paso1-fecha-venta"
            type="date"
            required
            value={fechaVenta}
            onChange={(e) => onFechaVentaChange(e.target.value)}
            className={`w-full px-4 py-3 ${FORM_BORDER} ${FORM_BORDER_HOVER} ${FOCUS_RING} ${FORM_RADIUS} bg-white`}
          />
        </div>
      </div>

      {/* Alerta de sin stock */}
      {sinStock && (
        <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-amber-800">Sin stock disponible</p>
            <p className="text-xs text-amber-600">Este producto no tiene unidades disponibles en ningún lote. Debes crear un nuevo lote de producción antes de registrar pedidos.</p>
          </div>
        </div>
      )}

      {/* Selector de lote (solo si hay múltiples lotes activos) */}
      {mostrarSelectorLote && (
        <div>
          <Select
            label="Lote de producción"
            options={lotesDelProducto.map((lote) => ({
              value: lote.id,
              label: `${lote.nombre} — Disponible: ${lote.stock_disponible} de ${lote.stock_inicial}${lote.stock_disponible === 0 ? ' (agotado)' : ''}`,
            }))}
            value={produccionId}
            onChange={(v) => onProduccionChange(v)}
            placeholder="Seleccionar lote..."
            disabled={isEditMode}
          />
        </div>
      )}

      {/* Indicador de lote auto-asignado (cuando no se muestra el selector) */}
      {!mostrarSelectorLote && lotesDelProducto.length === 1 && (
        <p className="text-xs text-slate-500 -mt-4">
          Lote asignado: <span className="font-medium text-slate-700">{lotesDelProducto[0].nombre}</span>
          <span className="text-slate-400"> — Disponible: {lotesDelProducto[0].stock_disponible} uds.</span>
        </p>
      )}

      {/* Selector de Cliente */}
      <SelectorClientePedido
        tipoCliente={tipoCliente}
        onTipoChange={onTipoClienteChange}
        miembroId={miembroId}
        onMiembroChange={onMiembroChange}
        nombreExterno={nombreExterno}
        onNombreExternoChange={onNombreExternoChange}
        miembroReferidoId={miembroReferidoId}
        onMiembroReferidoChange={onMiembroReferidoChange}
        lumisialId={lumisialId}
        initialMiembroNombre={initialMiembroNombre}
        initialMiembroReferidoNombre={initialMiembroReferidoNombre}
        sinAsociacionMiembro={sinAsociacionMiembro}
      />

      {/* Cantidad, Precio y Total en una fila */}
      <div className="flex gap-4">
        <div className="w-[20%] flex-shrink-0">
          <TextInput
            label="Cantidad"
            type="number"
            value={String(cantidad || '')}
            onChange={(v) => onCantidadChange(Number(v))}
            min={1}
            required
          />
        </div>

        <div className="w-[30%] flex-shrink-0">
          <p className="block text-sm font-semibold text-slate-700 mb-2">
            Precio Unitario
          </p>
          <div className="bg-slate-50 rounded-lg px-4 py-3 border border-slate-300">
            <span className="text-lg text-slate-700">
              ${precioUnitario.toLocaleString('es-CL')}
            </span>
          </div>
        </div>

        <div className="w-full">
          <p className="block text-sm font-semibold text-emerald-700 mb-2">
            Total
          </p>
          <div className="bg-emerald-50 rounded-lg px-4 py-3 border border-emerald-200">
            <span className="text-lg font-bold text-emerald-700">
              ${montoTotal.toLocaleString('es-CL')}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
